import { ShieldCheck, Award, MessageSquareQuote, Search } from 'lucide-react';
import { ScrollReveal, ScrollRevealGroup } from '@podus/ui';

const steps = [
  {
    number: '01',
    title: 'Submit Employee Details',
    description: 'Provide the essential information of the person you want screened.',
  },
  {
    number: '02',
    title: 'Consent & Verification',
    description:
      'We contact the employee to obtain consent, collect additional details, and secure a signed affidavit of undertaking.',
  },
  {
    number: '03',
    title: 'Field Intelligence & Evidence Gathering',
    description:
      'Our team goes on the ground to verify information, gather evidence, and build a comprehensive intelligence report.',
  },
];

const points = [
  {
    icon: ShieldCheck,
    title: 'We filter malicious intent',
    description: 'Our checks filter people with malicious intent from your domestic staff and employee pool.',
  },
  {
    icon: Award,
    title: '20+ years of experience',
    description: 'Over 20 years of intelligence gathering, working with reputable organisations.',
  },
  {
    icon: MessageSquareQuote,
    title: 'Behaviour changes',
    description: 'Vetted employees report: “I know this employment is different, I must come correct.”',
  },
  {
    icon: Search,
    title: 'A head start when it matters',
    description: 'If an incident occurs, our report helps investigators get started swiftly.',
  },
];

// "How it works" and "What makes us different" share one compact section so a
// visitor captures both at a single glance — two columns on desktop, stacked on
// mobile.
export default function HowAndDifferenceSection() {
  return (
    <section className="w-full bg-white px-4 md:px-6 lg:px-10 py-12 md:py-14">
      <div className="w-full mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
        {/* How it works */}
        <div>
          <ScrollReveal variant="fade-up">
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 md:mb-5">How it works</h2>
          </ScrollReveal>
          <div className="flex flex-col gap-3">
            <ScrollRevealGroup variant="fade-right" stagger={120}>
              {steps.map((step) => (
                <div
                  key={step.number}
                  className="group bg-gray-50 rounded-xl p-4 flex items-start gap-3 transition-all duration-300 hover:-translate-y-1 hover:bg-gray-100 hover:shadow-md"
                >
                  <span className="text-[#FF6B35] text-lg font-bold leading-none shrink-0 w-7 transition-transform duration-300 group-hover:scale-110">
                    {step.number}
                  </span>
                  <div>
                    <h3 className="text-sm md:text-[15px] font-bold text-gray-900 mb-0.5">{step.title}</h3>
                    <p className="text-gray-600 text-[13px] leading-snug">{step.description}</p>
                  </div>
                </div>
              ))}
            </ScrollRevealGroup>
          </div>
        </div>

        {/* What makes us different */}
        <div>
          <ScrollReveal variant="fade-up">
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 md:mb-5">What makes us different</h2>
          </ScrollReveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <ScrollRevealGroup variant="fade-up" stagger={100}>
              {points.map((point) => {
                return (
                  <div
                    key={point.title}
                    className="group h-full bg-[#F5F5F5] rounded-xl p-4 transition-all duration-300 hover:-translate-y-1 hover:shadow-md"
                  >
                    <div className="flex items-center gap-2.5 mb-2">
                      <h3 className="text-sm md:text-[15px] font-bold text-gray-900 leading-tight">{point.title}</h3>
                    </div>
                    <p className="text-gray-600 text-[13px] leading-snug">{point.description}</p>
                  </div>
                );
              })}
            </ScrollRevealGroup>
          </div>
        </div>
      </div>
    </section>
  );
}
